import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Mail, Phone, Building, Key, AlertTriangle, Lock, Database, PenTool, Save, Trash2 } from 'lucide-react';

import { toast } from 'sonner';
const Einstellungen = () => {  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    firma: '',
    buero_adresse: '',
    gdv_requestor: '',
    gdv_password: '',
    dat_customer_number: '',
    dat_requestor: '',
    dat_password: '',
    netcasion_email: '',
    netcasion_password: ''
  });
  
  // Signature state
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  
  const [loadingCredentials, setLoadingCredentials] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('autogutachter24_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      setFormData({
        vorname: userData.vorname || '',
        nachname: userData.nachname || '',
        email: userData.email || '',
        telefon: userData.telefon || '',
        firma: userData.firma || '',
        buero_adresse: userData.buero_adresse || '',
        gdv_requestor: userData.gdv_requestor || '',
        gdv_password: userData.gdv_password || '',
        dat_customer_number: userData.dat_customer_number || '',
        dat_requestor: userData.dat_requestor || '',
        dat_password: userData.dat_password || '',
        netcasion_email: userData.netcasion_email || '',
        netcasion_password: userData.netcasion_password || ''
      });
      
      // Load signature if exists
      if (userData.signature) {
        setSignatureImage(userData.signature);
      }
      
      // Load API credentials
      loadApiCredentials();
    }
  }, []);
  
  // Signature Drawing Functions
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureImage(null);
  };
  
  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const signatureData = canvas.toDataURL('image/png');
    setSignatureImage(signatureData);
    
    // Save to localStorage
    const updatedUser = { ...user, signature: signatureData };
    localStorage.setItem('autogutachter24_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    
    toast.success('Erfolg', {
      description: 'Unterschrift erfolgreich gespeichert',
    });
  };
  
  // Load signature on canvas when component mounts
  useEffect(() => {
    if (signatureImage && signatureCanvasRef.current) {
      const canvas = signatureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = signatureImage;
    }
  }, [signatureImage]);
  
  const loadApiCredentials = async () => {
    setLoadingCredentials(true);
    try {
      const token = localStorage.getItem('autogutachter24_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/settings/credentials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Update formData with API credentials
        setFormData(prev => ({
          ...prev,
          gdv_requestor: data.gdv?.requestor || '',
          gdv_password: data.gdv?.password || '',
          dat_customer_number: data.dat?.customer_number || '',
          dat_requestor: data.dat?.requestor || '',
          dat_password: data.dat?.password || '',
          netcasion_email: data.netcasion?.email || '',
          netcasion_password: data.netcasion?.password || ''
        }));
      }
    } catch (error) {
      console.error('Error loading API credentials:', error);
    } finally {
      setLoadingCredentials(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('autogutachter24_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Update localStorage with profile data
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('autogutachter24_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      // Save API credentials to backend
      // GDV Credentials
      if (formData.gdv_requestor || formData.gdv_password) {
        await fetch(`${backendUrl}/api/settings/credentials/gdv`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            requestor: formData.gdv_requestor,
            password: formData.gdv_password
          })
        });
      }
      
      // DAT Credentials
      if (formData.dat_customer_number || formData.dat_requestor || formData.dat_password) {
        await fetch(`${backendUrl}/api/settings/credentials/dat`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customer_number: formData.dat_customer_number,
            requestor: formData.dat_requestor,
            password: formData.dat_password
          })
        });
      }
      
      // net.casion Credentials
      if (formData.netcasion_email || formData.netcasion_password) {
        await fetch(`${backendUrl}/api/settings/netcasion`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: formData.netcasion_email,
            password: formData.netcasion_password,
            client_name: 'Autogutachter24',
            client_version: '1.0.0'
          })
        });
      }
      
      toast.success('Erfolg', {
        description: 'Profil und API-Zugangsdaten erfolgreich gespeichert',
      });
      
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Fehler', {
        description: 'Fehler beim Speichern der Daten'
      });
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Einstellungen</h1>
          <p className="text-gray-500 mt-1">Verwalten Sie Ihre Kontoeinstellungen</p>
        </div>

        {/* Profile Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <User className="w-5 h-5 text-blue-600" />
              <span>Profil Informationen</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="vorname" className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>Vorname</span>
                  </Label>
                  <Input
                    id="vorname"
                    name="vorname"
                    value={formData.vorname}
                    onChange={handleChange}
                    className="h-11"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nachname" className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>Nachname</span>
                  </Label>
                  <Input
                    id="nachname"
                    name="nachname"
                    value={formData.nachname}
                    onChange={handleChange}
                    className="h-11"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>E-Mail</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefon" className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>Telefon</span>
                </Label>
                <Input
                  id="telefon"
                  name="telefon"
                  type="tel"
                  value={formData.telefon}
                  onChange={handleChange}
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="firma" className="flex items-center space-x-2">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span>Firma</span>
                </Label>
                <Input
                  id="firma"
                  name="firma"
                  value={formData.firma}
                  onChange={handleChange}
                  className="h-11"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="buero_adresse" className="flex items-center space-x-2">
                  <Building className="w-4 h-4 text-gray-400" />
                  <span>Büro-Adresse</span>
                </Label>
                <Input
                  id="buero_adresse"
                  name="buero_adresse"
                  value={formData.buero_adresse}
                  onChange={handleChange}
                  placeholder="z.B. Musterstraße 123, 12345 Musterstadt"
                  className="h-11"
                  required
                />
                <p className="text-xs text-gray-500">
                  Diese Adresse wird für die automatische Entfernungsberechnung zu Kunden verwendet
                </p>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <Key className="w-4 h-4 text-gray-400" />
                  <span>Kundennummer</span>
                </Label>
                <Input
                  value={user.kundennummer}
                  disabled
                  className="bg-gray-100 h-11"
                />
                <p className="text-xs text-gray-500">Ihre Kundennummer kann nicht geändert werden</p>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 my-6"></div>
              
              {/* API Zugangsdaten Sektion */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center space-x-2">
                  <Database className="w-4 h-4" />
                  <span>API Zugangsdaten</span>
                </h3>
                <p className="text-xs text-blue-700">
                  Speichern Sie Ihre API-Zugangsdaten für externe Dienste (GDV, DAT, net.casion)
                </p>
              </div>

              {/* GDV Zugangsdaten */}
              <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                  <Database className="w-4 h-4" />
                  <span>GDV Zugangsdaten</span>
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="gdv_requestor" className="text-xs">
                      Benutzer (Requestor)
                    </Label>
                    <Input
                      id="gdv_requestor"
                      name="gdv_requestor"
                      type="text"
                      value={formData.gdv_requestor || ''}
                      onChange={handleChange}
                      placeholder="z.B. ZA0315P03012714"
                      className="h-10"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="gdv_password" className="text-xs">
                      Passwort
                    </Label>
                    <Input
                      id="gdv_password"
                      name="gdv_password"
                      type="password"
                      value={formData.gdv_password || ''}
                      onChange={handleChange}
                      placeholder="GDV Passwort"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              {/* DAT Zugangsdaten */}
              <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                  <Database className="w-4 h-4" />
                  <span>DAT Zugangsdaten</span>
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="dat_customer_number" className="text-xs">
                      DAT-Kundennummer
                    </Label>
                    <Input
                      id="dat_customer_number"
                      name="dat_customer_number"
                      type="text"
                      value={formData.dat_customer_number || ''}
                      onChange={handleChange}
                      placeholder="z.B. 1348925"
                      className="h-10"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="dat_requestor" className="text-xs">
                      Benutzer
                    </Label>
                    <Input
                      id="dat_requestor"
                      name="dat_requestor"
                      type="text"
                      value={formData.dat_requestor || ''}
                      onChange={handleChange}
                      placeholder="Benutzername"
                      className="h-10"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="dat_password" className="text-xs">
                      Passwort
                    </Label>
                    <Input
                      id="dat_password"
                      name="dat_password"
                      type="password"
                      value={formData.dat_password || ''}
                      onChange={handleChange}
                      placeholder="DAT Passwort"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              {/* net.casion Zugangsdaten */}
              <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                  <Database className="w-4 h-4" />
                  <span>net.casion Restwert Zugangsdaten</span>
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="netcasion_email" className="text-xs">
                      Email (Login)
                    </Label>
                    <Input
                      id="netcasion_email"
                      name="netcasion_email"
                      type="email"
                      value={formData.netcasion_email || ''}
                      onChange={handleChange}
                      placeholder="ihre-email@example.com"
                      className="h-10"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="netcasion_password" className="text-xs">
                      Passwort
                    </Label>
                    <Input
                      id="netcasion_password"
                      name="netcasion_password"
                      type="password"
                      value={formData.netcasion_password || ''}
                      onChange={handleChange}
                      placeholder="net.casion Passwort"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Profil speichern
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Signature Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <PenTool className="w-5 h-5 text-blue-600" />
              <span>Unterschrift des Sachverständigen</span>
            </CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              Ihre Unterschrift wird automatisch in allen generierten Gutachten angezeigt
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Signature Canvas */}
              <div className="bg-gray-50 border-2 border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-white p-4">
                  <div className="relative bg-white border-2 border-gray-300 rounded">
                    <canvas
                      ref={signatureCanvasRef}
                      width={700}
                      height={180}
                      className="w-full cursor-crosshair bg-white"
                      style={{ touchAction: 'none' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                    {/* Signature Line */}
                    <div className="absolute bottom-4 left-4 right-4 border-b-2 border-gray-400 pointer-events-none"></div>
                    <div className="absolute bottom-1 left-4 text-xs text-gray-500 pointer-events-none">
                      Unterschrift Sachverständiger
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-100 border-t border-gray-300 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-600">Zeichnen Sie mit der Maus oder Touch</span>
                    {signatureImage && (
                      <div className="flex items-center space-x-1 text-green-600 text-xs font-medium">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Gespeichert</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearSignature}
                      className="text-gray-600 hover:text-gray-800 h-9"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Löschen
                    </Button>
                    <Button
                      type="button"
                      onClick={saveSignature}
                      className="bg-blue-600 text-white hover:bg-blue-700 h-9"
                      size="sm"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Speichern
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Danger Zone */}
        <Card className="border-0 shadow-lg border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span>Gefahrenbereich</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Konto löschen</h3>
              <p className="text-sm text-gray-600 mb-4">
                Sobald Sie Ihr Konto löschen, gibt es kein Zurück mehr. Bitte seien Sie sich sicher.
              </p>
              <Button variant="destructive" className="bg-red-600 hover:bg-red-700">
                Konto löschen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Einstellungen;